import { useId, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { formatMoney, ownPeriodSpend } from '@/features/workspace-usage';
import { PeriodType } from '@/lib/api/generated/model';
import type {
  PeriodBounds,
  WorkflowLimitRow,
  WorkspaceLimitRow,
} from '@/lib/api/generated/model';

import {
  exceedsWorkspaceLimit,
  restartsPeriod,
  workflowWouldStop,
} from '../blockedForecast';
import { budgetSummarySentence } from '../budgetSummary';
import { useClearWorkflowLimit, useSetWorkflowLimit } from '../hooks/useLimits';
import { parseLimitAmount } from '../limitAmount';
import { LimitField } from './LimitField';
import { PeriodField } from './PeriodField';

/**
 * One workflow's cap (spec 080 FR-001, FR-007, FR-008).
 *
 * The tier **stacks**: this does not replace the workspace's limit, it sits inside it. So
 * the dialog names both figures — a cap read without the ceiling above it invites someone
 * to set a number that can never fire.
 *
 * Both warnings are computed from the rows already on the page, not from the mutation's
 * response. The service does report them — `WorkflowLimitView` carries `blocks_workflows`
 * and `exceeds_workspace_limit` — but it reports them for a cap it has *already applied*,
 * which tells an administrator what they have done rather than what they are about to do.
 * This is the same argument `blockedForecast.ts` makes for the organization default, and
 * the same conclusion.
 *
 * Neither warning blocks the save. Stopping a workflow mid-period is a legitimate and
 * often urgent act; the requirement is that nobody does it unknowingly.
 *
 * "No limit of its own" is a second way to remove this workflow's cap, alongside
 * `RemoveWorkflowLimitDialog`'s destructive confirm on the table row — checking it here
 * and saving is a non-destructive way to say "stop capping this workflow" without
 * leaving this dialog, mirroring the checkbox `OverrideDialog` added for the workspace
 * tier from the same mockup family (`.local/set-ws-budget-dlg.png`).
 */
export function WorkflowLimitDialog({
  row,
  workspaces,
  currency,
  readPeriod,
}: {
  row: WorkflowLimitRow;
  /** The console's own workspace rows, for the ceiling this cap sits under. */
  workspaces: readonly WorkspaceLimitRow[];
  currency: string;
  /** The period being read, which the cap's own period is said against (spec 237). */
  readPeriod: PeriodBounds;
}) {
  const [open, setOpen] = useState(false);
  // The figure the "stops now" warning below is judged by, said as such: "$3.00 today"
  // for a Daily cap read in October, not the month's (spec 237).
  const own = ownPeriodSpend(
    row.limit_period,
    readPeriod,
    row.limit_consumed_amount,
  );
  // This dialog only ever opens on an already-capped workflow, so it starts unchecked —
  // unlike `OverrideDialog`, where a row might already be following the default.
  const [noLimit, setNoLimit] = useState(false);
  const [draft, setDraft] = useState(row.limit);
  const [periodDraft, setPeriodDraft] = useState<PeriodType>(row.period_type);
  const [submitted, setSubmitted] = useState(false);
  const setLimit = useSetWorkflowLimit();
  const clearLimit = useClearWorkflowLimit();
  const pending = setLimit.isPending || clearLimit.isPending;
  const saveError = setLimit.error ?? clearLimit.error;
  const parsed = parseLimitAmount(draft);

  // Only after an attempt to save — a message beside a field somebody has not finished
  // typing in is an accusation, not help.
  const error = submitted && !noLimit && !parsed.ok ? parsed.reason : null;
  // A new period type starts the cap's period at zero, so that save stops nothing (238).
  const stops =
    !noLimit &&
    parsed.ok &&
    !restartsPeriod(row.period_type, periodDraft) &&
    workflowWouldStop(row, parsed.amount);
  const cannotFire =
    !noLimit &&
    parsed.ok &&
    exceedsWorkspaceLimit(row, workspaces, parsed.amount);
  const checkboxId = useId();

  const close = (next: boolean) => {
    if (pending) return;
    setOpen(next);
    if (!next) {
      setLimit.reset();
      clearLimit.reset();
      setSubmitted(false);
      // Back to what the workflow's limit actually is. Without this, cancelling an edit
      // and reopening presents the abandoned figure as the current cap — and saving
      // then applies a number the reader had already decided against.
      setDraft(row.limit);
      setPeriodDraft(row.period_type);
      setNoLimit(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger
        render={
          <Button
            variant="outline"
            size="sm"
            aria-label={`Change the limit for ${row.workflow_name}`}
          />
        }
      >
        Change
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Limit for {row.workflow_name}</DialogTitle>
          <DialogDescription>
            A workflow limit applies on top of its workspace&rsquo;s — a run
            must clear both. {row.workflow_name} has spent{' '}
            {own
              ? `${formatMoney(own.spend, currency)} ${own.phrase}`
              : `${formatMoney(row.spend, currency)} of this period`}{' '}
            in {row.workspace_name}.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitted(true);
            if (noLimit) {
              clearLimit.mutate(row.workflow_id, {
                onSuccess: () => {
                  close(false);
                },
              });
              return;
            }
            if (!parsed.ok) return;
            setLimit.mutate(
              {
                workflowId: row.workflow_id,
                amount: parsed.amount,
                periodType: periodDraft,
              },
              {
                onSuccess: () => {
                  close(false);
                },
              },
            );
          }}
        >
          <div className="border-border bg-muted/40 flex flex-col gap-1.5 rounded-md border px-3 py-3">
            <label
              htmlFor={checkboxId}
              className="flex items-center gap-2 text-sm font-medium"
            >
              <input
                id={checkboxId}
                type="checkbox"
                checked={noLimit}
                onChange={(event) => {
                  setNoLimit(event.target.checked);
                  setSubmitted(false);
                }}
                className="size-4"
              />
              No limit of its own
            </label>
            <p className="text-muted-foreground text-sm">
              This workflow will be bounded only by {row.workspace_name}&rsquo;s
              own limit.
            </p>
          </div>

          <LimitField
            label="Amount per period"
            value={draft}
            onChange={(next) => {
              setDraft(next);
              setSubmitted(false);
            }}
            currency={currency}
            error={error}
            disabled={noLimit}
            autoFocus={!noLimit}
          />

          <PeriodField
            value={periodDraft}
            onChange={setPeriodDraft}
            disabled={noLimit}
          />

          {/* The live summary sentence beneath the period control, hidden while
              removing the cap — the checkbox's own description above already says
              what applies instead. */}
          {!noLimit && parsed.ok ? (
            <p className="border-border bg-muted/40 rounded-md border px-3 py-2 text-sm">
              {budgetSummarySentence({
                name: row.workflow_name,
                amount: parsed.amount,
                periodType: periodDraft,
                spend: row.spend,
                currency,
              })}
            </p>
          ) : null}

          {/* FR-007. Said before the save, and the button beside it stays enabled. */}
          {stops ? (
            <p
              role="status"
              className="border-state-warning/40 rounded-md border border-dashed px-3 py-2 text-sm"
            >
              This stops the workflow immediately — it has already spent this
              much this period. Its workspace&rsquo;s other workflows keep
              running.
            </p>
          ) : null}

          {/*
            FR-008. Permitted rather than refused: the workspace limit can be lowered
            later, and refusing this would make an existing cap retroactively illegal.
            So it is stated, and saved.
          */}
          {cannotFire ? (
            <p
              role="status"
              className="border-border text-muted-foreground rounded-md border border-dashed px-3 py-2 text-sm"
            >
              This is at or above what {row.workspace_name} may spend, so it can
              never be the limit that stops a run. The workspace&rsquo;s own
              limit applies first.
            </p>
          ) : null}

          {saveError ? (
            <p role="alert" className="text-destructive text-sm">
              {saveError.message}
            </p>
          ) : null}

          <DialogFooter>
            <DialogClose
              render={<Button variant="outline" disabled={pending} />}
            >
              Cancel
            </DialogClose>
            <Button type="submit" disabled={pending}>
              {pending ? 'Saving…' : 'Save limit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
